import type {Entity, Expand, FlatExpand} from '../types';

import {not} from '../../utils/conditionalUtils';
import {isEntityId} from '../../utils/entity/isEntityId';

const expandedLookup = (
  id: string,
  expanded: {[key: string]: any},
  defaultValue: any,
) => expanded[id] || defaultValue;

export const expandObj = <T extends Entity, Expanded = any>(
  obj: T,
  {
    expand = {},
    expanded = {},
  }: {
    expand: Expand;
    expanded: {[key: string]: any};
  } = {
    expand: {},
    expanded: {},
  },
): T & Expanded => {
  if (!obj || !obj._id) {
    return obj as T & Expanded;
  }

  const objType = obj._id.split(':')[0]!;

  if (!expand[objType]) {
    return obj as T & Expanded;
  }

  let result = {...obj};

  const rootExpand = {
    [objType]: expand[objType],
    ...expand,
  };

  const doExpand = (target: any, _expand: Expand) => {
    const expandKeys = Object.keys(_expand);
    const expandKeysLength = expandKeys.length;

    if (!target || !target._id) {
      return target;
    }

    for (
      let expandKeysI = 0;
      expandKeysI < expandKeysLength;
      expandKeysI += 1
    ) {
      const targetType = target._id.split(':')[0];

      if (targetType !== expandKeys[expandKeysI]) {
        continue;
      }

      const expandFields = _expand[expandKeys[expandKeysI]!];
      const expandFieldLength = expandFields.length;

      for (
        let expandFieldsI = 0;
        expandFieldsI < expandFieldLength;
        expandFieldsI += 1
      ) {
        const expandField = expandFields[expandFieldsI];

        let field = expandField;
        let defaultValue = null;
        let innerExpand = {};
        let destKey = '';

        if (Array.isArray(expandField)) {
          [field, defaultValue, innerExpand] = expandField as any;
        }

        // dot notation supports only arrays for now
        if (field.includes('.')) {
          let innerPath = '';

          [field, innerPath] = field.split('.');

          const value = target[field];

          if (!Array.isArray(value)) {
            continue;
          }

          target[field] = value.map((item: any) => {
            const maybeArray = item[innerPath];
            const isMultipleIds = Array.isArray(maybeArray);
            const isSingleId = isEntityId(maybeArray);
            const shouldSkipExpanding = not(isMultipleIds) && not(isSingleId);

            if (shouldSkipExpanding) {
              return item;
            }

            const makeExpand = (id: string) =>
              doExpand(
                expandedLookup(id, expanded, defaultValue),
                innerExpand || {},
              );

            const expandedPath = innerPath.replace('_id', '');

            if (isSingleId) {
              /**
               * Covers case when `field.innerPath` is not an array of ids,
               * but single id.
               * For example conference expand `archives.file_id`.
               *
               * Pay attention!!! Test should use valid entity ids,
               * because algorithm rely on `isEntityId` check util.
               */
              return {
                ...item,
                [expandedPath]: makeExpand(maybeArray),
              };
            }

            if (isMultipleIds) {
              return {
                ...item,
                [expandedPath]: maybeArray.map(makeExpand),
              };
            }
          });

          continue;
        }

        [field, destKey] = field.split('->');

        const value = target[field];
        const destField = destKey
          ? field.replace(field, destKey)
          : field.replace('_id', '');

        if (Array.isArray(value)) {
          target[destField] = value.map((id: string) =>
            doExpand(
              expandedLookup(id, expanded, defaultValue),
              innerExpand || {},
            ),
          );
        } else if (innerExpand && Object.keys(innerExpand).length > 0) {
          target[destField] = doExpand(
            expandedLookup(target[field], expanded, defaultValue),
            innerExpand || {},
          );
        } else {
          target[destField] = expandedLookup(value, expanded, defaultValue);
        }
      }
    }

    return target;
  };

  result = doExpand(result, rootExpand);

  return result as T & Expanded;
};

type ExpandExtend = any[] | string;

export const flatExpand = (expand: Expand = {}): FlatExpand => {
  const innerExpands = {};

  const doFlat = (target: Expand): FlatExpand => {
    const targetCopy = {...target};

    Object.keys(targetCopy).forEach(key => {
      targetCopy[key] = targetCopy[key].map(item => {
        let result = item;
        let innerExpand: Expand | null = null;

        if (Array.isArray(item)) {
          [result, , innerExpand] = item as any;
        }

        if (innerExpand) {
          innerExpand = doFlat(innerExpand);

          Object.keys(innerExpand).forEach(_key => {
            if (innerExpands[_key]) {
              innerExpands[_key].push(...innerExpand![_key]);
            } else {
              innerExpands[_key] = innerExpand![_key];
            }
          });
        }

        return result.split('->')[0];
      });
    });

    return targetCopy as FlatExpand;
  };

  const flat = doFlat(expand);

  // removed duplicates
  Object.keys(innerExpands).forEach(key => {
    innerExpands[key] = Array.from(new Set(innerExpands[key]));
  });

  // merge inner expand values with the main expand values
  Object.keys(innerExpands).forEach(key => {
    if (flat[key]) {
      innerExpands[key] = Array.from(
        new Set(innerExpands[key].concat(flat[key])),
      );
    }
  });

  return {
    ...flat,
    ...innerExpands,
  };
};

export function extendExpand(
  expand: Expand,
  extend: ExpandExtend[],
  isMutual = false,
): Expand {
  const rootKey = Object.keys(expand)[0]!;
  const nextExpandBody = [...expand[rootKey], ...extend];

  if (isMutual) {
    expand[rootKey] = nextExpandBody;
  }

  return {[rootKey]: nextExpandBody};
}

export function omitExpand(expand: Expand, omitKeys: string[]): Expand {
  const rootKey = Object.keys(expand)[0]!;
  const nextExpandBody = expand[rootKey].filter(
    i => !omitKeys.includes(Array.isArray(i) ? i[0] : i),
  );

  return {[rootKey]: nextExpandBody};
}
